import type { PessoaDetalhe } from "@/api/contrato";
import { Botao } from "@/componentes/Botao";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { useModalNaUrl } from "@/componentes/useModalNaUrl";
import { data, documento } from "@/formato";
import { useSessao } from "@/sessao/useSessao";
import { CartaoDoCertificado } from "./CartaoDoCertificado";
import { EditarCedente } from "./EditarCedente";
import { pendenciaOferece } from "./pendencias";
import { TrocarCertificado } from "./TrocarCertificado";
import { useCedenteDaRota } from "./useCedenteDaRota";

/*
 * `/cedentes/:id/dados`: identidade, contato e endereço, em caixas de campo.
 *
 * O cedente anterior ao endereço tipado chega com os campos vazios, e string vazia
 * passaria pelo travessão da `CaixaDeCampo`: `preenchido` a devolve como ausente.
 */
function preenchido(valor: string | undefined): string | undefined {
  return valor === undefined || valor.trim() === "" ? undefined : valor;
}

function cep(valor: string): string | undefined {
  return preenchido(valor.replace(/^(\d{5})(\d{3})$/, "$1-$2"));
}

function telefone(valor: string | undefined): string | undefined {
  return preenchido(valor?.replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3"));
}

function GradeDeDados({ pessoa }: { pessoa: PessoaDetalhe }) {
  const { endereco } = pessoa;

  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 [&>*]:min-w-0 [&>*]:break-words">
      <CaixaDeCampo rotulo="Razão social" className="col-span-2">
        {pessoa.razao_social}
      </CaixaDeCampo>
      <CaixaDeCampo rotulo="Nome fantasia">{preenchido(pessoa.nome_fantasia)}</CaixaDeCampo>
      <CaixaDeCampo rotulo="Documento" dados>
        {documento(pessoa.documento)}
      </CaixaDeCampo>

      <CaixaDeCampo rotulo="E-mail" className="col-span-2">
        {preenchido(pessoa.email)}
      </CaixaDeCampo>
      <CaixaDeCampo rotulo="Telefone" dados>
        {telefone(pessoa.telefone)}
      </CaixaDeCampo>
      <CaixaDeCampo rotulo="Cadastrado em" dados>
        {data(pessoa.criado_em)}
      </CaixaDeCampo>

      <CaixaDeCampo rotulo="Logradouro" className="col-span-2">
        {preenchido(endereco.logradouro)}
      </CaixaDeCampo>
      <CaixaDeCampo rotulo="Número" dados>
        {preenchido(endereco.numero)}
      </CaixaDeCampo>
      <CaixaDeCampo rotulo="Complemento">{preenchido(endereco.complemento)}</CaixaDeCampo>

      <CaixaDeCampo rotulo="Bairro">{preenchido(endereco.bairro)}</CaixaDeCampo>
      <CaixaDeCampo rotulo="Cidade">{preenchido(endereco.cidade)}</CaixaDeCampo>
      <CaixaDeCampo rotulo="UF">{preenchido(endereco.uf)}</CaixaDeCampo>
      <CaixaDeCampo rotulo="CEP" dados>
        {cep(endereco.cep)}
      </CaixaDeCampo>
    </div>
  );
}

export function AbaDeDados() {
  const { pessoa, prontidao, consulta } = useCedenteDaRota();
  const { pode } = useSessao();
  const modal = useModalNaUrl();
  const ehAdmin = pode("admin");
  const certificado = pessoa?.certificado ?? null;

  if (pessoa === undefined && consulta.error !== null) return <FaixaDeErro erro={consulta.error} />;

  return (
    <div className="flex flex-col gap-3">
      {/* Com `Editar dados` na lista de pendências, o segundo botão seria a mesma ação. */}
      {ehAdmin && !pendenciaOferece(prontidao, "Editar dados") && (
        <div className="flex justify-end">
          <Botao
            tom="secundario"
            disabled={pessoa === undefined}
            onClick={() => modal.abrirModal({ abrir: "editar" })}
          >
            Editar
          </Botao>
        </div>
      )}

      {/* A altura de quatro linhas de caixa, com os vãos. */}
      {pessoa === undefined ? <Esqueleto className="h-[240px]" /> : <GradeDeDados pessoa={pessoa} />}

      {/* Só com linha: o normal é o certificado do convênio, e este nasce da conferência. */}
      {certificado !== null && <CartaoDoCertificado certificado={certificado} />}

      {modal.abrir === "editar" && ehAdmin && pessoa !== undefined && (
        <EditarCedente pessoa={pessoa} aoFechar={modal.fechar} />
      )}

      {modal.abrir === "certificado" && ehAdmin && pessoa !== undefined && certificado !== null && (
        <TrocarCertificado
          alvo={{ nivel: "cedente", id: pessoa.id }}
          nome={pessoa.razao_social}
          troca
          aoFechar={modal.fechar}
        />
      )}
    </div>
  );
}
